import { AtomicBlockUtils, EditorState, Modifier, RichUtils, SelectionState } from 'draft-js';
import { Map } from 'immutable';
import { createElement, createRef, PureComponent } from 'react';

import DefaultToolbar from './DefaultToolbar';
import EquationToolbar from './EquationToolbar';
import ImageToolbar from './ImageToolbar';

import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

export default class Toolbar extends PureComponent {
  state = { active: false };
  toolbarWrapper = createRef();

  updateBlockData = data => {
    const { editorState, onChange } = this.props;
    const { focusedBlock } = this.props;
    const selection = focusedBlock ? SelectionState.createEmpty(focusedBlock.key) : editorState.getSelection();
    onChange(
      EditorState.push(
        editorState,
        Modifier.mergeBlockData(editorState.getCurrentContent(), selection, Map(data)),
        'change-block-data',
      ),
    );
  };

  addAtomicBlock = (type, data) => {
    const { editorState, onChange } = this.props;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(type, 'IMMUTABLE', data);
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    let newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    if (RichUtils.getCurrentBlockType(newEditorState) === 'atomic') {
      newEditorState = EditorState.forceSelection(
        newEditorState,
        newEditorState.getSelection().merge({ focusOffset: 1, anchorOffset: 1 }),
      ); //RichUtils.insertSoftNewline(newEditorState);
    }
    newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    onChange(newEditorState);
  };

  setActive = (active, toolbarTypeOverride = null) => {
    const { onToggleNestedEditorFocus } = this.props;
    this.setState({ active, toolbarTypeOverride });

    onToggleNestedEditorFocus && onToggleNestedEditorFocus(active);
  };

  setHidden = hidden => this.setState({ hidden });

  render() {
    const { anchorEl, open } = this.props;
    const { active, hidden } = this.state;
    return (
      <Popper
        anchorEl={anchorEl}
        className={hidden ? 'popperHidden' : ''}
        css={popperRootCss}
        modifiers={POPPER_MODIFIERS}
        open={open || active}
        placement="top-start"
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            {this.renderToolbar()}
          </Fade>
        )}
      </Popper>
    );
  }

  renderToolbar() {
    const {
      activeInlineImage,
      anchorEl,
      editorState,
      focusedBlock,
      nested,
      onChange,
      onFocus,
      plugins,
      toolbarClassName,
    } = this.props;
    const { toolbarTypeOverride } = this.state;
    const toolbarType = toolbarTypeOverride || (focusedBlock && focusedBlock.type) || 'DEFAULT';

    const contentState = editorState.getCurrentContent();
    const focusedKey =
      (focusedBlock && focusedBlock.key) || (editorState.getSelection() && editorState.getSelection().getStartKey());
    const block = contentState.getBlockForKey(focusedKey);
    return (
      <Paper
        className={toolbarClassName}
        css={rootCss}
        elevation={4}
        onClick={e => e.stopPropagation()}
        onMouseUp={e => e.preventDefault()}
        ref={this.toolbarWrapper}
        style={{ width: anchorEl ? anchorEl.clientWidth : null }}
      >
        {createElement(blockToolbars[toolbarType], {
          activeInlineImage,
          block,
          editorState,
          nested,
          onAddAtomicBlock: this.addAtomicBlock,
          onUpdateBlockData: this.updateBlockData,
          onChange,
          onFocus,
          onSetActive: this.setActive,
          onSetHidden: this.setHidden,
          plugins,
        })}
      </Paper>
    );
  }
}

const blockToolbars = {
  DEFAULT: DefaultToolbar,
  IMAGE: ImageToolbar,
  EQUATION: EquationToolbar,
  EDITOR: DefaultToolbar,
};

const POPPER_MODIFIERS = [
  { name: 'flip', enabled: false },
  {
    name: 'offset',
    options: { offset: [0, 4] }, // leave some space between toolbar and top of editor
  },
  {
    name: 'preventOverflow',
    options: {
      altAxis: true,
      padding: { top: 57 },
    },
  },
];

const popperRootCss = ({ zIndex }) => ({
  zIndex: zIndex.modal + 1, // want toolbar to show up when editor is in a modal

  '&.popperHidden': {
    visibility: 'hidden',
    opacity: 0,
  },
});

const rootCss = ({ shape }) => ({
  display: 'flex',
  overflowX: 'auto',
  MsOverflowStyle: 'none', // IE and Edge
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': { display: 'none' }, //Chrome, Safari and Opera
  borderRadius: shape.borderRadiusSm,
  '& > *': { flexShrink: 0 },
});
